import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import {
	GenerateRecommendedPetitionAnglesJob,
	type GenerateRecommendedPetitionAnglesJobMutation,
	type GenerateRecommendedPetitionAnglesJobMutationVariables,
} from './generateRecommendedPetitionAnglesJob.graphql';

export type GenerateRecommendedPetitionAnglesJobInput = {
	mainPrompt: string;
	personalStory?: string | null;
};

export type GenerateRecommendedPetitionAnglesJobReturn = {
	responseId: string;
};

export async function generateRecommendedPetitionAnglesJob(
	input: GenerateRecommendedPetitionAnglesJobInput,
	utilityContext: UtilityContext,
	track: TrackingFn,
): Promise<GenerateRecommendedPetitionAnglesJobReturn> {
	const {
		gql: { fetch },
		errorReporter,
	} = utilityContext;

	void track('sap_ai_petition_recommendations_generate');

	try {
		if (!input.mainPrompt) {
			throw new Error('Main prompt is required');
		}

		const { data } = await fetch<
			GenerateRecommendedPetitionAnglesJobMutation,
			GenerateRecommendedPetitionAnglesJobMutationVariables
		>({
			query: GenerateRecommendedPetitionAnglesJob,
			path: '/generate-recommended-petition-angles-job',
			variables: {
				input: {
					mainPrompt: input.mainPrompt,
					personalStory: input.personalStory || '',
				},
			},
			rejectOnError: true,
		});

		if (data.generateRecommendedPetitionAnglesJob.__typename !== 'GenerateRecommendedPetitionAnglesJobSuccess') {
			throw new Error(
				`Failed to generate recommended petition angles job: ${data.generateRecommendedPetitionAnglesJob.__typename}`,
			);
		}
		void track('sap_ai_petition_recommendations_generate_job_loaded');
		return {
			responseId: data.generateRecommendedPetitionAnglesJob.responseId,
		};
	} catch (e: unknown) {
		const error = e instanceof Error ? e : new Error('An unknown error occurred', { cause: e });

		void track('sap_ai_petition_recommendations_generate_job_error');

		void errorReporter.report(error);
		throw error;
	}
}
